import React, { useEffect, useState } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useConnectors } from '../../../providers/connectors/hook/useConnectors';
import { GET_SHOPIFY_FILTERS } from 'src/utils/gql/queries';
import { ConnectorType, Query, Split } from 'src/utils/gql/types';

import {
  BackButton,
  CalendarWrapper,
  MainButtonGroupWrapper,
  ParametrsWrapper,
  SaveButton,
} from '../styled';
import {
  DashboardForm,
  InputName,
  ParametrsMobileContentProps,
} from '../types';
import { getTitle } from '../utils';

import CustomCalendar from './CustomCalendar';
import { FilterMetrics } from './FilterMetrics';
import { Filters } from './Filters';
import { MetricsInput } from './MetricsInput';
import { MetricsMobileContent } from './MetricsMobileContent';
import MetricsModal from './MetricsModal';
import { SplitsFilter } from './SplitsFilter';

export const ParametrsMobileContent = ({
  onBackClick,
  onSave,
  googleAdsMetrics,
  shopifyFilterValues,
  magentoFilterValues,
  connectors,
}: ParametrsMobileContentProps) => {
  const { watch, resetField } = useFormContext<DashboardForm>();
  const [isShownMetricFilters, setIsShownMetricFilters] =
    useState<boolean>(true);

  const snackbar = useSnackbar();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const filters = watch(InputName.filters);

  const handleMetricsBackClick = () => {
    resetField('metrics');

    setIsModalOpen(false);
  };

  const handleMetricsSaveClick = () => {
    resetField('metrics', { defaultValue: watch('metrics') });

    setIsModalOpen(false);
  };

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message = graphQLErrors.length ? graphQLErrors[0].message : errorText;

    snackbar(message);
  };

  const { splits: allConnectorSplits } = useConnectors();

  const { data: shopifyFiltersData } = useQuery<
    Pick<Query, 'getShopifyFilters'>
  >(GET_SHOPIFY_FILTERS, {
    onError,
  });

  const shopifySplits: Split[] =
    (shopifyFiltersData?.getShopifyFilters ?? [])
      .map(({ key }) => ({
        key,
        label: getTitle(key),
        id: key,
        connectorType: ConnectorType.SHOPIFY,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const splits = allConnectorSplits || [];

  useEffect(() => {
    if (!isShownMetricFilters && !filters.length) setIsShownMetricFilters(true);
  }, [isShownMetricFilters, filters.length]);

  // temporary solution
  const googleAdsMetricId =
    googleAdsMetrics.find(({ category }) => category === 'metric')?.id || '';

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        height="100%"
      >
        <Box display="flex" alignItems="center" justifyContent="center">
          <CalendarWrapper
            padding="20px"
            width="max-content"
            flexWrap="wrap"
            justifyContent="center"
          >
            <Box display="flex" gap="8px" alignItems="center">
              <Typography fontSize="18px" fontWeight={400} lineHeight="24px">
                From
              </Typography>
              <CustomCalendar name={InputName.startDate} />
            </Box>
            <Box display="flex" gap="8px" alignItems="center">
              <Typography fontSize="18px" fontWeight={400} lineHeight="24px">
                to
              </Typography>
              <CustomCalendar name={InputName.endDate} />{' '}
            </Box>
          </CalendarWrapper>
        </Box>

        <ParametrsWrapper>
          <SplitsFilter splits={[...splits, ...shopifySplits]} />
          <MetricsInput
            metricsIsShown={isModalOpen}
            closeMetrics={() => setIsModalOpen(false)}
            showMetrics={() => setIsModalOpen(true)}
          />
          <Filters
            shopifyFilterValues={shopifyFilterValues}
            magentoFilterValues={magentoFilterValues}
            toogleFilterMetrics={() => setIsShownMetricFilters((prev) => !prev)}
            isShownMetricFilters={isShownMetricFilters}
            googleAdsMetricId={googleAdsMetricId}
            connectors={connectors}
          />
          {!!filters.length &&
            isShownMetricFilters &&
            filters.map((filter, idx) => {
              return (
                <FilterMetrics
                  key={filter.id}
                  filter={filter}
                  formulaItemIndex={idx}
                />
              );
            })}
        </ParametrsWrapper>
        <MainButtonGroupWrapper>
          <BackButton onClick={onBackClick}>Back</BackButton>
          <SaveButton onClick={onSave}>Save</SaveButton>
        </MainButtonGroupWrapper>
      </Box>

      <MetricsModal open={isModalOpen} closeModal={handleMetricsBackClick}>
        {isModalOpen && (
          <MetricsMobileContent
            onBackClick={handleMetricsBackClick}
            onSave={handleMetricsSaveClick}
            connectors={connectors}
          />
        )}
      </MetricsModal>
    </Box>
  );
};
